import { addValidationRule } from 'formsy-react'
import { I18n } from 'react-redux-i18n'

const createUtcTimestamp = (
  dateStr: string,
  timeStr?: string,
  isEndTime = false
): number => {
  const [year, month, day] = dateStr.split('-').map(Number)

  let hour = 0
  let minute = 0

  if (timeStr === undefined || timeStr === '') {
    if (isEndTime) {
      hour = 23
      minute = 59
    }
  } else {
    ;[hour, minute] = timeStr.split(':').map(Number)
  }

  return Date.UTC(year, month - 1, day, hour, minute)
}

addValidationRule(
  'endDateNotInPast',
  (values: any, value: string, blockId: string) => {
    if (!value) return true

    const endTime = values[`${blockId}.end_time`]
    const endUtcTimestamp = createUtcTimestamp(value, endTime, true)
    const nowUtc = Date.now()

    if (endUtcTimestamp < nowUtc) {
      return I18n.t('validations.end_cannot_be_past')
    }

    return true
  }
)

addValidationRule(
  'startDateNotInPast',
  (values: any, value: string, blockId: string) => {
    if (!value) return true

    const startTime = values[`${blockId}.start_time`]
    const startUtcTimestamp = createUtcTimestamp(value, startTime, false)
    const nowUtc = Date.now()

    if (startUtcTimestamp < nowUtc) {
      return I18n.t('validations.start_cannot_be_past')
    }

    return true
  }
)

addValidationRule(
  'startBeforeEnd',
  (
    values: any,
    value: string,
    config: { blockId: string; endDate?: string; endTime?: string }
  ) => {
    if (!value) return true

    const startDate = value
    const startTime = values[`${config.blockId}.start_time`]
    const actualEndDate = values[`${config.blockId}.end_date`] || config.endDate
    const actualEndTime = values[`${config.blockId}.end_time`] || config.endTime

    if (startDate && actualEndDate) {
      const startUtcTimestamp = createUtcTimestamp(startDate, startTime, false)
      const endUtcTimestamp = createUtcTimestamp(
        actualEndDate,
        actualEndTime,
        true
      )

      if (startUtcTimestamp >= endUtcTimestamp) {
        return I18n.t('validations.start_must_be_before_end')
      }
    }

    return true
  }
)

addValidationRule(
  'dateRequiredIfTime',
  (values: any, value: string, blockId: string) => {
    // This validation is for time fields - it checks if a date is present when time is entered
    if (!value) return true // If no time is entered, validation passes

    // Extract the field name to determine if this is start_time or end_time
    const timeField = Object.keys(values).find(
      key => values[key] === value && key.includes('_time')
    )
    if (!timeField) return true

    const isStartTime = timeField.includes('start_time')
    const dateFieldName = isStartTime
      ? `${blockId}.start_date`
      : `${blockId}.end_date`
    const correspondingDate = values[dateFieldName]

    if (!correspondingDate) {
      return isStartTime
        ? I18n.t('validations.start_date_required_when_time_set')
        : I18n.t('validations.end_date_required_when_time_set')
    }

    return true
  }
)

addValidationRule('startDatetimeNotInPast', (values: any, value: string) => {
  if (!value) return true

  const startUtcTimestamp = new Date(value).getTime()
  const nowUtc = Date.now()

  if (startUtcTimestamp < nowUtc) {
    return I18n.t('validations.start_cannot_be_past')
  }

  return true
})

addValidationRule('endDatetimeNotInPast', (values: any, value: string) => {
  if (!value) return true

  const endUtcTimestamp = new Date(value).getTime()
  const nowUtc = Date.now()

  if (endUtcTimestamp < nowUtc) {
    return I18n.t('validations.end_cannot_be_past')
  }

  return true
})

addValidationRule(
  'startDatetimeBeforeEnd',
  (values: any, value: string, config: { blockId: string }) => {
    if (!value) return true

    const endDatetimeValue = values[`${config.blockId}.end_at`]
    if (!endDatetimeValue) return true

    const startUtcTimestamp = new Date(value).getTime()
    const endUtcTimestamp = new Date(endDatetimeValue).getTime()

    if (startUtcTimestamp >= endUtcTimestamp) {
      return I18n.t('validations.start_must_be_before_end')
    }

    return true
  }
)

addValidationRule('datetimeRequired', (values: any, value: string) => {
  if (!value || value === '') {
    return I18n.t('validations.is_required')
  }
  return true
})

export { createUtcTimestamp }
